import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supervisorApi } from '@/features/workspace/api';
import { routes } from '@/lib/routes';
import '@/features/student/sv121.css';

type LearnerRow = {
  id: string;
  academia_id: string;
  full_name: string;
  education_level: string;
  program: string;
  topic: string | null;
  progress_pct: number;
  course_year?: number | null;
  next_deadline?: string | null;
  guidance_kind?: string;
};

const FILTERS = [
  { id: 'ALL', label: 'Все' },
  { id: 'BACHELOR', label: 'Бакалавры' },
  { id: 'MASTER', label: 'Магистранты' },
  { id: 'DOCTORAL', label: 'Докторанты' },
] as const;

const levelLabel = (level: string) => {
  if (level === 'DOCTORAL') return 'Докторант';
  if (level === 'MASTER') return 'Магистрант';
  return 'Бакалавр';
};

const levelAccent = (level: string) => {
  if (level === 'DOCTORAL') return '#49ddb7';
  if (level === 'MASTER') return '#9e80ff';
  return '#41d2ff';
};

export const SupervisedLearnersPage = ({ onClose }: { onClose: () => void }) => {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<(typeof FILTERS)[number]['id']>('ALL');
  const [students, setStudents] = useState<LearnerRow[]>([]);
  const [notice, setNotice] = useState('');

  useEffect(() => {
    supervisorApi
      .module('supervision', { tab: 'students' })
      .then((payload) => setStudents((payload.items as LearnerRow[] | undefined) ?? []))
      .catch(() => setNotice('Не удалось открыть список обучающихся.'));
  }, []);

  const visible = useMemo(
    () => students.filter((item) => filter === 'ALL' || item.education_level === filter),
    [filter, students],
  );

  const openWork = (id: string, section: string) => {
    navigate(`${routes.supervisor}/supervision?student=${encodeURIComponent(id)}&section=${section}`);
  };

  return (
    <div className="sv121-root" role="dialog" aria-modal="true">
      <div className="sv121-shell">
        <header className="sv121-top">
          <button type="button" className="back" onClick={onClose}>←</button>
          <div className="sv121-brand">
            <small>Научное руководство</small>
            <h1>Обучающиеся под руководством</h1>
            <p>Бакалавры, магистранты и докторанты в научном руководстве</p>
          </div>
          <div className="sv121-top-actions">
            <button type="button" className="sv121-btn" onClick={onClose}>Закрыть</button>
          </div>
        </header>
        <main className="sv121-main">
          <section className="sv121-panel" style={{ ['--c' as string]: '#49ddb7' }}>
            <span className="sv121-eyebrow">Отдельный контур</span>
            <h2>Фильтр по уровню обучения</h2>
            <p>Этот список не смешивается с общим кафедральным списком «Докторанты кафедры» и с кабинетом «Обучающийся».</p>
            <div className="sv121-tabs" style={{ marginTop: 14 }}>
              {FILTERS.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className={filter === item.id ? 'active' : ''}
                  onClick={() => setFilter(item.id)}
                >
                  {item.label}
                </button>
              ))}
            </div>
            {notice ? <div className="sv121-callout" style={{ ['--c' as string]: '#e8b33b' }}>{notice}</div> : null}
            {visible.length === 0 && !notice ? <div className="sv121-empty">В этом уровне пока нет обучающихся.</div> : null}
            <div className="sv121-learner-grid">
              {visible.map((item) => (
                <article key={item.id} className="sv121-card" style={{ ['--c' as string]: levelAccent(item.education_level) }}>
                  <span className="sv121-badge info">
                    {levelLabel(item.education_level)} · {item.course_year || '—'} курс
                  </span>
                  <h3>{item.full_name}</h3>
                  <p>{item.program}</p>
                  <p><strong>Тема:</strong> {item.topic || 'Тема на согласовании'}</p>
                  <p><strong>Вид руководства:</strong> {item.guidance_kind || 'Индивидуальное научное руководство'}</p>
                  <p><strong>Ближайший срок:</strong> {item.next_deadline || 'Срок не назначен'}</p>
                  <div className="sv121-progress" style={{ ['--p' as string]: `${item.progress_pct}%` }}><span /></div>
                  <p>Выполнение: <strong>{item.progress_pct}%</strong></p>
                  <div className="sv121-actions">
                    <button type="button" className="sv121-btn" onClick={() => openWork(item.id, 'resources')}>Материалы</button>
                    <button type="button" className="sv121-btn" onClick={() => navigate(`${routes.supervisor}/connections`)}>Сообщения</button>
                    <button type="button" className="sv121-btn" onClick={() => openWork(item.id, 'meetings')}>Встречи</button>
                    <button type="button" className="sv121-btn gold" onClick={() => openWork(item.id, 'plan')}>Индивидуальный план</button>
                  </div>
                </article>
              ))}
            </div>
          </section>
        </main>
      </div>
    </div>
  );
};
